import React from 'react';
import moment from 'moment';
import { useSession } from '@/hooks/useSession';
import { PlayfulCard, PlayfulCardButton } from '@/components/custom/playful-card';
import { AvatarWrapper } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import duelsIcon from '@/assets/images/icons/duelos.png';

function calculateTimeRemaining(expiration) {
  const now = new Date();
  const expirationTime = new Date(expiration);

  const differenceInMs = expirationTime - now;

  const days = Math.floor(differenceInMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((differenceInMs / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((differenceInMs / (1000 * 60)) % 60);

  switch (true) {
    case days === 1:
      return '1 dia';
    case days > 1:
      return `${days} dias`;
    case hours === 1:
      return '1 hora';
    case hours > 1:
      return `${hours} horas`;
    case minutes === 1:
      return '1 minuto';
    case minutes > 1:
      return `${minutes} minutos`;
    default:
      return 'Tempo expirado';
  }
}

export function DuelsPlayfulCard({ duel, handleStart, showResults, status }) {
  const { session } = useSession();
  const userAvatar = session.user.image.url;
  const opponentAvatar = duel?.opponent?.image?.url;

  const isMyTurnDone = duel?.['rounds-completed-by-me'] === duel?.['duel-rounds-count'];
  const isExpired = moment(new Date()).isAfter(moment(duel?.['expiration-date']));
  const timeRemaining = calculateTimeRemaining(duel?.['expiration-date']);
  const showResultsAction = isExpired || isMyTurnDone;

  const badgeText = isMyTurnDone && status !== 'completed' ? 'Vez do oponente' : status === 'completed' ? 'Finalizado' : `${duel?.['rounds-completed-by-me']}/${duel?.['duel-rounds-count']} rodadas`;

  const creatorName = duel?.['creator-id'] === session.user.id ? session.user.name : duel?.opponent?.name;
  const creatorAvatar = duel?.['creator-id'] === session.user.id ? userAvatar : opponentAvatar;
  const invitedName = duel?.['invited-id'] === session.user.id ? session.user.name : duel?.opponent?.name;
  const invitedAvatar = duel?.['invited-id'] === session.user.id ? userAvatar : opponentAvatar;

  const handleAction = () => (showResultsAction ? showResults(duel) : handleStart(duel));

  return (
    <PlayfulCard
      badge={{ text: badgeText, variant: status === 'completed' ? 'success-soft' : 'amber-soft', className: 'rounded-full px-2.5 py-1 text-2xs font-extrabold normal-case' }}
      className="p-6"
    >
      <img
        src={duelsIcon}
        alt=""
        className="h-16 w-16 object-contain"
      />

      <h3
        className="mb-0 mt-3 line-clamp-2 text-base font-extrabold text-neutral-900"
        title={duel?.['parent-taxonomy']?.name}
      >
        {duel?.['parent-taxonomy']?.name}
      </h3>

      <div className="mt-4 flex items-center gap-2 rounded-xl bg-white p-3">
        <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
          <div className="relative">
            <AvatarWrapper
              src={creatorAvatar}
              text={creatorName}
            />
            <span className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-sm leading-none">✨</span>
          </div>
          <span
            className="line-clamp-2 max-w-full text-center text-xs font-bold text-neutral-dark"
            title={creatorName}
          >
            {creatorName}
          </span>
        </div>

        <div className="flex shrink-0 items-center gap-2 text-lg font-extrabold text-neutral-900">
          <span>{duel?.['creator-id'] === session.user.id ? duel?.['rounds-won-by-me'] : duel?.opponent?.['rounds-won']}</span>
          <span className="text-sm font-bold text-danger">x</span>
          <span>{duel?.['invited-id'] === session.user.id ? duel?.['rounds-won-by-me'] : duel?.opponent?.['rounds-won']}</span>
        </div>

        <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
          <AvatarWrapper
            src={invitedAvatar}
            text={invitedName}
          />
          <span
            className="line-clamp-2 max-w-full text-center text-xs font-bold text-neutral-dark"
            title={invitedName}
          >
            {invitedName}
          </span>
        </div>
      </div>

      <p className={cn('mb-0 mt-3 text-center text-xs font-semibold', isExpired || timeRemaining.includes('minuto') ? 'text-danger' : 'text-neutral-dark')}>{isExpired ? 'Duelo expirado' : `Expira em ${timeRemaining}`}</p>

      <div className="mt-auto pt-4">
        <PlayfulCardButton onClick={handleAction}>{showResultsAction ? 'Ver resultados' : 'Jogar'}</PlayfulCardButton>
      </div>
    </PlayfulCard>
  );
}
